"use client";

import type { Transaction, TokenBalance } from "@/lib/blockscout-api";
import { formatBalance } from "@/util/balance/formatBalance";
import { Drawer, DrawerContent, DrawerHeader, DrawerBody, Button, Link, Tooltip, Tabs, Tab, Table, TableHeader, TableColumn, TableBody, TableRow, TableCell, Badge, ScrollShadow, Image, cn, DrawerFooter } from "@heroui/react";
import Aggregator from "app/swap/_Aggregator";
import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import type { Address } from "viem";
import { useConnection } from "wagmi";
import { chainSettings, type ChainType, fallbackChainId } from "@/constants/chain-specific";
import { useSearchToken } from "@/hooks/useSearchToken";
import { DOGE_TOKENS, SHIB_TOKENS } from "@/constants/project-specific/tokens";
import type { TokenConfigType, TokenIndex } from "@/constants/project-specific";
import { usePortfolioOpen } from "@/hooks/modals/usePortfolioOpen";
import { RESET } from "jotai/utils";
import { useAddressTokenBalances } from "@/hooks/portfolio/useFetchTokenBalances";
import { useAddressTransactions } from "@/hooks/portfolio/useFetchAddressTxs";
import { truncAddress } from "@/util/address";
import { getNativePrices } from "@/lib/price-data/geckoPrices";
import type { NativeTokenPrices } from "@/constants/project-specific/farms/types";
import { Icon } from "@iconify/react/dist/iconify.js";
import { txLink } from "@/util/explorerLinks";
import useBalances from "@/hooks/useBalances";
import { useSelectedToken } from "@/hooks/portfolio/useSelectedToken";
import { useSelectedNFTs } from "@/hooks/portfolio/useSelectedNFTs";
import { NFTCollectionGridPaginated } from "./NFTCollectionGridPaginated";
import { BulkTransferModal } from "./BulkTransferModal";
import { useIsMobile } from '../../hooks/utility/useIsMobile';
import { getTokenStats } from "@/lib/price-data/getTokenStats";
import { isBulkTransferSupported } from "@/constants/chain-specific/bulkTransferContracts";
import { useNFTCollectionBalanceVerification } from "@/hooks/portfolio/useNFTCollectionBalanceVerification";
import { getCollectionByAddress, getCollectionByName, getMarketplaceCollections } from "@/constants/marketplace/collections";
import { shibarium } from "viem/chains";
import { AnimatePresence, motion } from "motion/react";
import { CollectionOffersView } from "./CollectionOffersView";
import { useCollectionOffers } from "@/hooks/marketplace/useCollectionOffers";
import { useQuery } from "wagmi/query";
const fmt = new Intl.NumberFormat('en', {
  notation: 'compact',
  compactDisplay: 'short',
  minimumFractionDigits: 0,
  maximumFractionDigits: 1
});
export const findTokenByAddress = (address: Address, chainId: ChainType): TokenIndex[keyof TokenIndex] | undefined => {
  if (!address) return undefined;
  switch (Number(chainId)) {
    case 2000:
      return Object.values(DOGE_TOKENS).find(token => token.address.toLowerCase() === address.toLowerCase());
    default:
      return Object.values(SHIB_TOKENS).find(token => token.address.toLowerCase() === address.toLowerCase());
  }
};
interface Props {
  isOpen?: boolean | undefined;
  onOpenChange?: (isOpen: boolean) => void;
  address: `0x${string}`;
}

// Define SortDescriptor type locally
type SortDescriptor = {
  column: string;
  direction: 'ascending' | 'descending';
};
export default function PortfolioDrawer({
  isOpen,
  onOpenChange,
  address
}: Props) {
  // Helper function to get the correct address field from API response
  const getTokenAddress = (token?: TokenBalance['token']) => {
    return token?.address || (token as any)?.address_hash;
  };
  const {
    chainId
  } = useConnection();

  // Simple swipe gesture for closing drawer
  const touchStartRef = useRef<{
    x: number;
    y: number;
  } | null>(null);
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY
    };
  }, []);
  const handleTouchEnd = useCallback((e_0: React.TouchEvent) => {
    if (!touchStartRef.current) return;
    const touch_0 = e_0.changedTouches[0];
    const deltaX = touch_0.clientX - touchStartRef.current.x;
    const absDeltaY = Math.abs(touch_0.clientY - touchStartRef.current.y);

    // Swipe right to close (80px+ rightward, minimal vertical movement)
    if (deltaX > 80 && absDeltaY < 50) {
      onOpenChange?.(false);
    }
    touchStartRef.current = null;
  }, [onOpenChange]);
  const isMobile = useIsMobile();
  const [isLoading, setIsLoading] = useState(true);
  // const [tokenBalances, setTokenBalances] = useState<TokenBalance[]>([]);
  const {
    selectedToken,
    setSelectedToken,
    selectedNft,
    setSelectedNft
  } = useSelectedToken();
  const {
    selectedCount,
    openBulkTransferModal
  } = useSelectedNFTs();
  const [viewingCollectionOffers, setViewingCollectionOffers] = useState(false);
  const [nativePrices, setNativePrices] = useState<NativeTokenPrices | undefined>(undefined);
  const [tokenPrices, setTokenPrices] = useState<Record<string, number> | undefined>(undefined);
  const {
    token: token_0
  } = useSearchToken((selectedToken?.token ? getTokenAddress(selectedToken.token) : undefined) as Address, chainId ?? fallbackChainId);
  const {
    selectedTab,
    setSelectedTab
  } = usePortfolioOpen();

  // Handle tab changes
  const handleTabChange = (key: "portfolio" | "txs" | "nfts") => {
    setSelectedTab(key);
    // Reset collection offers view when changing tabs
    setViewingCollectionOffers(false);
  };

  // Reset collection offers view when selectedNft changes
  useEffect(() => {
    setViewingCollectionOffers(false);
  }, [selectedNft]);

  // Sync table selection state with selected items
  useEffect(() => {
    if (selectedToken) {
      const key_0 = getTokenRowKey(selectedToken);
      setSelectedTokenKeys(new Set([key_0]));
    } else {
      setSelectedTokenKeys(new Set());
    }
  }, [selectedToken]);
  useEffect(() => {
    if (selectedNft) {
      const key_1 = getCollectionRowKey(selectedNft);
      setSelectedNftKeys(new Set([key_1]));
    } else {
      setSelectedNftKeys(new Set());
    }
  }, [selectedNft]);
  const {
    data: tokenBalances,
    isLoading: isBalancesLoading
  } = useAddressTokenBalances(address, chainId ?? fallbackChainId);

  // Type assertion to fix TypeScript inference issue
  type UserBalances = {
    tokens: TokenBalance[] | null;
    nfts: {
      erc721: TokenBalance[];
      erc1155: TokenBalance[];
    };
  };
  const typedTokenBalances = tokenBalances as UserBalances | undefined;
  const {
    data: transactions
  } = useAddressTransactions(address, chainId ?? fallbackChainId);

  // Keep table loading state in sync with balances loading
  useEffect(() => {
    setIsLoading(Boolean(isBalancesLoading));
  }, [isBalancesLoading]);

  // NFT collection balance verification
  const {
    verifiedBalances,
    hasAnyDiscrepancy,
    isLoading: isVerifyingBalances,
    refetch: refetchNFTBalances
  } = useNFTCollectionBalanceVerification(typedTokenBalances?.nfts?.erc721, typedTokenBalances?.nfts?.erc1155, address, chainId ?? fallbackChainId, isOpen);

  // Disable portfolio offers summary for now - individual collection offers work correctly
  const offerSummaries = new Map();

  // Collection-specific offers when viewing a collection - stable enabled condition
  const collectionOffersEnabled = useMemo(() => isOpen && selectedTab === "nfts" && !!selectedNft, [isOpen, selectedTab, selectedNft]);
  const {
    nftsWithOffers: selectedCollectionNftsWithOffers,
    loading: collectionOffersLoading,
    totalOffersCount: selectedCollectionOffersCount
  } = useCollectionOffers(selectedNft?.token?.address || '', selectedNft?.token?.name || '', address, chainId ?? fallbackChainId, collectionOffersEnabled);
  const {
    data: tokenStats
  } = useQuery({
    queryKey: ['tokenStats', chainId, address],
    queryFn: () => {
      const tokens = Array.isArray(typedTokenBalances?.tokens) ? typedTokenBalances.tokens.map((token_1: TokenBalance) => ({
        address: token_1.token.address,
        decimals: Number(token_1.token.decimals),
        symbol: token_1.token.symbol,
        name: token_1.token.name
      }) as TokenConfigType) : [];
      return getTokenStats(chainId ?? fallbackChainId, tokens);
    },
    enabled: isOpen && !!chainId && Array.isArray(typedTokenBalances?.tokens) && (typedTokenBalances?.tokens?.length ?? 0) > 0,
    staleTime: 1000 * 60 * 30,
    // 30 minutes
    retry: true,
    retryOnMount: true,
    retryDelay: attempt => Math.min(attempt > 1 ? 2 ** attempt * 10000 : 10000, 30 * 10000)
  });
  useEffect(() => {
    if (typedTokenBalances?.tokens === null || typedTokenBalances?.tokens === undefined || typedTokenBalances?.tokens.length === 0) return;
    const fetchNativePrices = async () => {
      if (!chainId) return;
      if (tokenStats) {
        setTokenPrices(Object.fromEntries(Object.entries(tokenStats).map(([addr, stats]) => [addr, stats.price])));
      }
      const nativePrice = await getNativePrices([chainId]);
      setNativePrices(nativePrice);
      // console.log(nativePrice);
      // console.log("token prices: ", tokenStats);
    };
    fetchNativePrices();
  }, [chainId, typedTokenBalances, tokenStats]);
  const {
    balances,
    refresh: refreshBalances
  } = useBalances(typedTokenBalances?.tokens?.map((token_2: TokenBalance) => token_2.token.address as Address) ?? [], address, undefined, chainId ?? fallbackChainId as ChainType);

  // Add sortDescriptor state for tokens and collections with correct types
  const [tokenSortDescriptor, setTokenSortDescriptor] = useState<SortDescriptor>({
    column: 'valueColumn',
    direction: 'ascending'
  });
  const [nftSortDescriptor, setNftSortDescriptor] = useState<SortDescriptor>({
    column: 'nftsName',
    direction: 'ascending'
  });

  // Add selection state for tables
  const [selectedTokenKeys, setSelectedTokenKeys] = useState<Set<string>>(new Set());
  const [selectedNftKeys, setSelectedNftKeys] = useState<Set<string>>(new Set());

  // Key helpers to avoid duplicate keys and preserve rendering even if address is missing
  const getTokenRowKey = (item: TokenBalance): string => {
    const addr_0 = getTokenAddress(item?.token);
    const id = item?.token_id;

    // Prefer address if available
    if (typeof addr_0 === 'string' && addr_0.length > 0 && addr_0 !== '0x') {
      return addr_0.toLowerCase();
    }

    // Fallback to token_id if available
    if (typeof id === 'string' && id.length > 0) {
      return `tokenid-${id.toLowerCase()}`;
    }

    // Create a unique fallback key using symbol, name, and a hash
    const symbol = item?.token?.symbol ?? 'unknown';
    const name = item?.token?.name ?? 'unknown';
    const value = item?.value ?? '0';

    // Create a more unique key by including value and using a counter
    const fallbackKey = `${symbol}-${name}-${value}`.toLowerCase().replace(/[^a-z0-9-]/g, '');
    return fallbackKey;
  };
  const getCollectionRowKey = (item_0: TokenBalance): string => {
    const addr_1 = (item_0 as any)?.token?.address;
    const id_0 = (item_0 as any)?.token_id;
    const fallback = item_0?.token?.name ?? 'collection';
    return String(typeof addr_1 === 'string' && addr_1.length ? addr_1 : id_0 ?? fallback).toLowerCase();
  };
  const getUsdValue = (token_3: TokenBalance) => {
    const address_0 = getTokenAddress(token_3?.token);
    if (!address_0 || typeof address_0 !== 'string' || address_0.length === 0) {
      return 0; // No price data for tokens without addresses
    }
    const price = tokenPrices?.[address_0.toLowerCase()] ?? 0;
    const bal = balances?.[address_0 as Address]?.balance?.bigInt ?? token_3.value ?? 0;
    const decimals = Number(token_3?.token?.decimals ?? 18);
    return price * (Number(bal) / 10 ** decimals);
  };
  const sortedTokens = useMemo(() => {
    if (!typedTokenBalances?.tokens) return [];
    // Deduplicate by address or token_id fallback to avoid duplicate keys
    const dedup = new Map<string, TokenBalance>();
    for (const t of typedTokenBalances.tokens) {
      const key_2 = getTokenRowKey(t);
      if (!dedup.has(key_2)) dedup.set(key_2, t);
    }
    const tokens_0 = Array.from(dedup.values());
    if (tokenSortDescriptor.column === "name") {
      tokens_0.sort((a, b) => {
        const nameA = a.token.name || a.token.symbol || '';
        const nameB = b.token.name || b.token.symbol || '';
        return tokenSortDescriptor.direction === "ascending" ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
      });
    } else if (tokenSortDescriptor.column === "valueColumn") {
      tokens_0.sort((a_0, b_0) => {
        const valueA = getUsdValue(a_0);
        const valueB = getUsdValue(b_0);
        return tokenSortDescriptor.direction === "descending" ? valueA - valueB : valueB - valueA;
      });
    }
    return tokens_0;
  }, [typedTokenBalances?.tokens, tokenSortDescriptor, tokenPrices, balances, tokenPrices]);
  const sortedCollections = useMemo(() => {
    // Merge all, allow missing address but dedupe by address or token_id fallback
    const merged = [...(typedTokenBalances?.nfts?.erc721 ?? []), ...(typedTokenBalances?.nfts?.erc1155 ?? [])];
    const dedupedMap = new Map<string, typeof merged[number]>();
    for (const c of merged) {
      const key_3 = getCollectionRowKey(c);
      if (!dedupedMap.has(key_3)) dedupedMap.set(key_3, c);
    }

    // For Shibarium, add marketplace collections with verified non-zero balances
    // that aren't already in the API response (handles API indexer being behind)
    if (chainId === shibarium.id) {
      const marketplaceCollections = getMarketplaceCollections(chainId);
      for (const collection of marketplaceCollections) {
        if (!collection.address) continue;
        const addrLower = collection.address.toLowerCase();

        // Skip if already in the dedupedMap from API response
        if (dedupedMap.has(addrLower)) continue;

        // Check if we have a verified non-zero balance for this collection
        const verifiedBalance = verifiedBalances.get(addrLower as Address);
        if (verifiedBalance && (verifiedBalance.realTimeBalance > 0 || verifiedBalance.isLoading)) {
          // Create a TokenBalance-like object for this collection
          const syntheticCollection: TokenBalance = {
            token: {
              address: collection.address,
              name: collection.name,
              symbol: collection.symbol || '',
              type: collection.type || 'ERC-721',
              decimals: '0'
            },
            value: verifiedBalance.realTimeBalance.toString(),
            token_id: collection.address,
            token_instance: {
              metadata: {
                token_instances: []
              }
            } as any
          };
          dedupedMap.set(addrLower, syntheticCollection);
        }
      }
    }
    const collections = Array.from(dedupedMap.values());
    if (nftSortDescriptor.column === 'nftsName') {
      collections.sort((a_1, b_1) => {
        const nameA_0 = a_1.token.name || '';
        const nameB_0 = b_1.token.name || '';
        return nftSortDescriptor.direction === 'ascending' ? nameA_0.localeCompare(nameB_0) : nameB_0.localeCompare(nameA_0);
      });
    } else if (nftSortDescriptor.column === 'nftsBalance') {
      collections.sort((a_2, b_2) => {
        const balanceA = parseInt(a_2.value);
        const balanceB = parseInt(b_2.value);
        return nftSortDescriptor.direction === 'ascending' ? balanceA - balanceB : balanceB - balanceA;
      });
    }
    return collections;
  }, [typedTokenBalances?.nfts, nftSortDescriptor, chainId, verifiedBalances]);
  const handleTokenClick = (token_4: TokenBalance) => {
    // Close drawer
    console.log("token: ", token_4);
    if (selectedToken?.token?.address === token_4?.token?.address) {
      setSelectedToken(RESET);
    } else {
      setSelectedToken(token_4);
    }
  };
  if (typedTokenBalances?.tokens === null) {
    return null;
  }
  return <>
      <Drawer hideCloseButton isOpen={isOpen} onOpenChange={open => {
      onOpenChange?.(open);
    }} size="3xl" backdrop="transparent" classNames={{
      body: "h-fit"
    }}>
        <DrawerContent className="max-h-dvh flex flex-col" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
          {onClose => <>
                <DrawerHeader className="sticky top-0 z-50 flex flex-row gap-2 px-2 py-2 justify-between backdrop-blur-md bg-background/70">
                  <Tooltip content="Close">
                    <Button isIconOnly className="text-default-400" size="sm" color="danger" variant="light" onPress={onClose} radius="full">
                      <svg fill="none" role="img" aria-label="Close Button" height="20" stroke="red" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="20" xmlns="http://www.w3.org/2000/svg">
                        <path d="m13 17 5-5-5-5M6 17l5-5-5-5" />
                      </svg>
                    </Button>
                  </Tooltip>
                  <div className="flex-1 flex items-center">
                    <div className="flex-1 flex justify-center min-w-[200px]">
                      {/* Tabs */}
                      <Tabs aria-label="Portfolio Tabs" variant="underlined" className="max-w-[200px]" selectedKey={selectedTab} onSelectionChange={key_4 => handleTabChange(key_4 as "nfts" | "txs" | "portfolio")}>
                        <Tab key="portfolio" title="Tokens" aria-label="Tokens in your Wallet" />
                        <Tab key="nfts" title="NFTs" aria-label="NFTs in your Wallet" />
                        <Tab key="txs" title="TXs" aria-label="Your Transaction History" />
                      </Tabs>
                    </div>

                    {/* NFT Actions: Optimization Toggle and Bulk Transfer */}
                    <div className="flex items-center gap-2 min-w-fit">


                      {/* Shopping Cart Icon for NFT Bulk Transfer */}

                      <Tooltip color="foreground" content={`${selectedCount} NFT${selectedCount !== 1 ? 's' : ''} selected for bulk transfer`}>
                        <Badge content={selectedCount} color="danger" size="sm" className={cn("mr-4", selectedTab === "nfts" && selectedCount > 0 && chainId && isBulkTransferSupported(chainId) ? "" : "invisible")} aria-label={`${selectedCount} NFT${selectedCount !== 1 ? 's' : ''} selected for bulk transfer`}>
                          <Button onPress={openBulkTransferModal} className={cn("relative mr-4", selectedTab === "nfts" && selectedCount > 0 && chainId && isBulkTransferSupported(chainId) ? "" : "invisible")} isIconOnly size="sm" color="primary" variant="flat" aria-label="Bulk Transfer NFTs">
                            <Icon icon="mdi:cart" className="text-lg" />
                          </Button>
                        </Badge>
                      </Tooltip>

                    </div>
                  </div>
                </DrawerHeader>
                <DrawerBody className="p-0 flex-1 overflow-hidden">
                  <div className="px-0 h-full flex flex-col">
                    <AnimatePresence mode="wait">
                      {selectedTab === "portfolio" ? <motion.div key="portfolio" initial={{
                  opacity: 0,
                  x: 20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} exit={{
                  opacity: 0,
                  x: -20
                }} transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 30
                }} className="h-full flex flex-col">
                          <AnimatePresence mode="wait">
                            {selectedToken?.token && getTokenAddress(selectedToken.token) ? <motion.div initial={{
                      opacity: 0,
                      y: 20
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} exit={{
                      opacity: 0,
                      y: -20
                    }} transition={{
                      type: "spring",
                      stiffness: 300,
                      damping: 30
                    }} className="h-full">
                                <motion.div initial={{
                        opacity: 0,
                        y: -20
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} exit={{
                        opacity: 0,
                        y: 20
                      }} transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 30
                      }} className="flex items-center bg-black/70">
                                  <Button size="sm" variant="light" color="secondary" startContent={<Icon icon="mdi:arrow-left" />} onPress={() => setSelectedToken(RESET)} aria-label="Go Back to Token Portfolio">
                                    Go Back
                                  </Button>
                                  <span className="text-sm md:text-lg font-medium w-auto line-clamp-1">{selectedToken.token.name}</span>
                                </motion.div>
                                <ScrollShadow size={15} className="h-full w-full">
                                  <motion.div initial={{
                          opacity: 0,
                          scale: 0.95
                        }} animate={{
                          opacity: 1,
                          scale: 1
                        }} exit={{
                          opacity: 0,
                          scale: 0.95
                        }} transition={{
                          type: "spring",
                          stiffness: 300,
                          damping: 30,
                          delay: 0.1
                        }}>
                                    <Aggregator display={{
                            showTicker: false,
                            showPremium: false,
                            showBubbles: false
                          }} key={`${getTokenAddress(selectedToken.token) ?? 'unknown'}-agg`} outToken={(() => {
                            const address_1 = getTokenAddress(selectedToken?.token);
                            return findTokenByAddress(address_1 as Address, chainId ?? fallbackChainId) ?? {
                              address: address_1 as Address,
                              decimals: Number(selectedToken?.token?.decimals ?? 18),
                              symbol: selectedToken?.token?.symbol ?? '',
                              name: selectedToken?.token?.name ?? '',
                              logo: token_0?.logo ?? ''
                            } as TokenConfigType;
                          })()} priceData={nativePrices && tokenPrices && {
                            nativePrices,
                            tokenPrices
                          }} onTxConfirmed={refreshBalances} />
                                  </motion.div>
                                </ScrollShadow>
                              </motion.div> : selectedToken && selectedToken.token && !getTokenAddress(selectedToken.token) ? <motion.div initial={{
                      opacity: 0,
                      y: 20
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} exit={{
                      opacity: 0,
                      y: -20
                    }} transition={{
                      type: "spring",
                      stiffness: 300,
                      damping: 30
                    }} className="h-full flex flex-col items-center justify-center p-8">
                                <div className="text-center">
                                  <h3 className="text-lg font-medium mb-2">
                                    {selectedToken.token.name || 'Selected Token'}
                                  </h3>
                                  <p className="text-default-500 mb-4">
                                    This token doesn't have a contract address and cannot be traded.
                                  </p>
                                  <Button size="sm" variant="light" color="secondary" startContent={<Icon icon="mdi:arrow-left" />} onPress={() => setSelectedToken(RESET)}>
                                    Go Back
                                  </Button>
                                </div>
                              </motion.div> : <ScrollShadow size={15} className="h-full w-full">
                                {typedTokenBalances?.tokens ? <Table isCompact fullWidth key="tokens-table" removeWrapper className="h-full" selectionBehavior="replace" selectionMode="single" selectedKeys={selectedTokenKeys} onSelectionChange={keys => {
                        const newKeys = new Set(typeof keys === 'string' ? [keys] : Array.from(keys).map(k => String(k)));
                        setSelectedTokenKeys(newKeys);

                        // Handle selection change
                        if (newKeys.size === 0) {
                          // No selection - reset selected token if currently selected
                          if (selectedToken) {
                            setSelectedToken(RESET);
                          }
                        } else {
                          // Get the selected key
                          const selectedKey = Array.from(newKeys)[0];
                          const selectedTokenData = sortedTokens.find(t_0 => getTokenRowKey(t_0) === selectedKey);
                          if (selectedTokenData && selectedTokenData !== selectedToken) {
                            // Set new selection
                            setSelectedToken(selectedTokenData);
                          }
                        }
                      }} sortDescriptor={tokenSortDescriptor} onSortChange={d => setTokenSortDescriptor({
                        column: String(d.column),
                        direction: d.direction
                      })} onRowAction={key_5 => {
                        // Double-click action - navigate to token details/swap
                        const targetKey = String(key_5);
                        const tokenData = sortedTokens.find(t_1 => getTokenRowKey(t_1) === targetKey);
                        if (tokenData) {
                          setSelectedToken(tokenData);
                        }
                      }}>
                                    <TableHeader>
                                      <TableColumn key="name" allowsSorting>
                                        {isMobile ? 'Symbol' : 'Name'}
                                      </TableColumn>
                                      <TableColumn key="balanceColumn" allowsSorting>
                                        Balance
                                      </TableColumn>
                                      <TableColumn key="valueColumn" allowsSorting>
                                        Value
                                      </TableColumn>
                                    </TableHeader>
                                    <TableBody isLoading={isLoading} items={sortedTokens}>
                                      {item_1 => <TableRow key={getTokenRowKey(item_1)} className="cursor-pointer">
                                          <TableCell className="overflow-hidden truncate">
                                            <div className="flex items-center gap-2">
                                              {(() => {
                                  const address_2 = getTokenAddress(item_1?.token);
                                  if (!address_2 || typeof address_2 !== 'string' || address_2.length === 0) return null;
                                  const tokenConfig = findTokenByAddress(address_2 as Address, chainId ?? fallbackChainId);
                                  if (!tokenConfig?.logo) return null;
                                  return <img src={tokenConfig.logo} alt={item_1?.token?.name ?? 'Token'} className="w-6 h-6 rounded-full" />;
                                })()}
                                              <span className="text-white text-ellipsis line-clamp-1 max-w-screen-md">
                                                {item_1?.token?.name ?? 'Unknown Token'}
                                              </span>
                                            </div>
                                          </TableCell>
                                          <TableCell>
                                            {(() => {
                                if (!item_1) return '0';
                                const address_3 = getTokenAddress(item_1?.token);
                                const decimals_0 = Number(item_1?.token?.decimals ?? 18);
                                let balance = 0;
                                if (address_3 && typeof address_3 === 'string' && address_3.length > 0 && balances) {
                                  // Use wagmi balance if available and token has address
                                  balance = Number(balances[address_3 as Address]?.balance?.bigInt ?? item_1?.value ?? 0);
                                } else {
                                  // Fallback to API value for tokens without addresses
                                  balance = Number(item_1?.value ?? 0);
                                }
                                const formatted = fmt.format(balance / 10 ** decimals_0);
                                return `${formatted} ${isMobile ? '' : item_1?.token?.symbol ?? ''}`;
                              })()}
                                          </TableCell>
                                          <TableCell>
                                            {(() => {
                                const usdValue = getUsdValue(item_1);
                                if (usdValue >= 0.01) {
                                  return `$${usdValue.toFixed(2)}`;
                                }
                                return null;
                              })()}
                                          </TableCell>
                                        </TableRow>}
                                    </TableBody>
                                  </Table> : null}
                              </ScrollShadow>}
                          </AnimatePresence>
                        </motion.div> : selectedTab === "nfts" ? <motion.div key="nfts" initial={{
                  opacity: 0,
                  x: 20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} exit={{
                  opacity: 0,
                  x: -20
                }} transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 30
                }} className="h-full flex flex-col">
                          {selectedNft && selectedNft.token ? <div className="h-full">
                                <motion.div initial={{
                      opacity: 0,
                      y: -10
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} exit={{
                      opacity: 0,
                      y: 10
                    }} transition={{
                      type: "spring",
                      stiffness: 300,
                      damping: 30
                    }} className="">
                                  <div className="mb-4 flex items-center justify-between bg-black/70 p-2">
                                    <div className="flex items-center gap-2">
                                      <Button size="sm" variant="light" color="secondary" startContent={<Icon icon="mdi:arrow-left" />} onPress={() => {
                            setSelectedNft(RESET);
                            setViewingCollectionOffers(false);
                          }} aria-label="Go Back to NFT Portfolio">
                                        Go Back
                                      </Button>
                                      <span className="text-sm md:text-lg font-medium text-start w-auto line-clamp-1">{selectedNft.token.name}</span>
                                    </div>

                                    {/* Offers Button */}
                                    {selectedCollectionOffersCount > 0 && !viewingCollectionOffers && <Button size="sm" color="success" variant="flat" startContent={<Icon icon="solar:hand-money-bold" />} onPress={() => setViewingCollectionOffers(true)} isLoading={collectionOffersLoading} className="mr-2">
                                        {selectedCollectionOffersCount} Offer{selectedCollectionOffersCount !== 1 ? 's' : ''}
                                      </Button>}
                                  </div>
                                  {(() => {
                        const contractAddress = getTokenAddress(selectedNft.token);

                        // Try to extract contract address from token_instances if not available in token
                        let fallbackContractAddress = contractAddress;
                        // Defensive: check all possible undefineds and types
                        // token_instances is not typed, so we need to check if metadata is an object and has token_instances as an array
                        const tokenInstances = selectedNft.token_instance && typeof selectedNft.token_instance.metadata === "object" && selectedNft.token_instance.metadata !== null && Array.isArray((selectedNft.token_instance.metadata as {
                          token_instances?: unknown;
                        }).token_instances) ? (selectedNft.token_instance.metadata as {
                          token_instances: any[];
                        }).token_instances : [];
                        if (!contractAddress && tokenInstances.length > 0 && typeof tokenInstances[0] === "object" && tokenInstances[0] !== null) {
                          const firstInstance = tokenInstances[0] as Record<string, any>;
                          // Check multiple possible locations for contract address
                          fallbackContractAddress = firstInstance?.token?.address || firstInstance?.token?.address_hash || firstInstance?.contract_address || firstInstance?.contractAddress ||
                          // Sometimes the contract address might be in the parent collection
                          selectedNft.token_id; // token_id might actually be the contract address
                        }

                        // If still no contract address, try to find it by collection name from our static data
                        if (!fallbackContractAddress && selectedNft.token.name && chainId) {
                          console.log(`🔍 Looking up collection "${selectedNft.token.name}" on chainId ${chainId}`);
                          let knownCollection = getCollectionByName(selectedNft.token.name, chainId);

                          // If exact name match fails, try fuzzy matching for common variations
                          if (!knownCollection) {
                            const tokenName = selectedNft.token.name.toLowerCase();
                            // Try variations like "DorkWadz" -> "Dork Wadz" 
                            if (tokenName.includes('dorkwadz') || tokenName.includes('dork wadz')) {
                              knownCollection = getCollectionByName('Dork Wadz', chainId) || getCollectionByName('DorkWadz', chainId) || getCollectionByName('DorkWadz DPIX', chainId);
                            }
                          }
                          if (knownCollection) {
                            fallbackContractAddress = knownCollection.address;
                            console.log(`📍 Found contract address for "${selectedNft.token.name}" -> "${knownCollection.name}": ${knownCollection.address}`);
                          } else {
                            console.warn(`⚠️ No collection found for "${selectedNft.token.name}" on chainId ${chainId}`);
                          }
                        }
                        const finalContractAddress = fallbackContractAddress || contractAddress;
                        if (!finalContractAddress) {
                          return <div className="flex items-center justify-center h-full">
                                          <p className="text-default-500">No contract address found for this collection</p>
                                          <p className="text-xs text-default-400 mt-2">
                                            Collection: {selectedNft.token.name}
                                          </p>
                                        </div>;
                        }
                        return viewingCollectionOffers ? <CollectionOffersView contractAddress={finalContractAddress as string} collectionName={selectedNft.token.name} nftsWithOffers={selectedCollectionNftsWithOffers} onBack={() => setViewingCollectionOffers(false)} /> : <NFTCollectionGridPaginated contractAddress={finalContractAddress as Address} collectionName={selectedNft.token.name} chainId={chainId ?? fallbackChainId} userAddress={address} knownBalance={parseInt(selectedNft.value) || undefined} />;
                      })()}
                                </motion.div>
                              </div> : <div className="h-full pb-2">
                              <ScrollShadow size={15} className="h-full w-full">
                                <div className="">
                                  <Table isCompact fullWidth key="nfts-table" removeWrapper sortDescriptor={nftSortDescriptor} selectionBehavior="replace" selectionMode="single" selectedKeys={selectedNftKeys} onSelectionChange={keys_0 => {
                          const newKeys_0 = new Set(typeof keys_0 === 'string' ? [keys_0] : Array.from(keys_0).map(k_0 => String(k_0)));
                          setSelectedNftKeys(newKeys_0);

                          // Handle selection change
                          if (newKeys_0.size === 0) {
                            // No selection - reset selected NFT if currently selected
                            if (selectedNft) {
                              setSelectedNft(RESET);
                            }
                          } else {
                            // Get the selected key - use sortedCollections which includes synthetic collections
                            const selectedKey_0 = Array.from(newKeys_0)[0];
                            const selectedCollection = sortedCollections.find(t_2 => getCollectionRowKey(t_2) === selectedKey_0);
                            if (selectedCollection && selectedCollection !== selectedNft) {
                              // Set new selection
                              setSelectedNft(selectedCollection);
                            }
                          }
                        }} onSortChange={d_0 => setNftSortDescriptor({
                          column: String(d_0.column),
                          direction: d_0.direction
                        })} onRowAction={key_6 => {
                          // Double-click action - use sortedCollections which includes synthetic collections
                          const targetKey_0 = String(key_6);
                          const collection_0 = sortedCollections.find(t_3 => getCollectionRowKey(t_3) === targetKey_0);
                          if (collection_0) {
                            setSelectedNft(collection_0);
                          }
                        }}>
                                    <TableHeader>
                                      <TableColumn key="nftsName" allowsSorting>
                                        Name
                                      </TableColumn>
                                      <TableColumn key="nftsBalance" allowsSorting>
                                        Balance
                                      </TableColumn>
                                    </TableHeader>
                                    <TableBody isLoading={isLoading} items={sortedCollections} emptyContent={`No NFT's Detected in ${truncAddress(address)}`}>
                                      {item_2 => {
                              const key_7 = getCollectionRowKey(item_2);
                              const addrCandidate = (item_2?.token?.address || item_2?.token_id) as string;
                              const contractAddress_0 = addrCandidate as Address;
                              const verifiedBalance_0 = typeof item_2?.token?.address === 'string' ? verifiedBalances.get(contractAddress_0) : undefined;
                              // Prevent showing 0 when verifiedBalance is loading or undefined
                              const apiBalance = parseInt(item_2.value) || 0;
                              const displayBalance = verifiedBalance_0?.isLoading ? apiBalance // Show API balance while loading
                              : verifiedBalance_0?.realTimeBalance ?? apiBalance;

                              // Debug logging for balance resets
                              if (displayBalance === 0 && apiBalance > 0) {
                                console.warn(`Balance reset detected for ${item_2.token.name}:`, {
                                  contractAddress: contractAddress_0,
                                  apiBalance,
                                  verifiedBalance: verifiedBalance_0?.realTimeBalance,
                                  isLoading: verifiedBalance_0?.isLoading,
                                  hasDiscrepancy: verifiedBalance_0?.hasDiscrepancy
                                });
                              }
                              const isERC1155 = item_2.token.type === "ERC-1155";
                              return <TableRow key={key_7} className="cursor-pointer">
                                            <TableCell>
                                              <div className="flex items-center gap-2">
                                                {(() => {
                                      if (!item_2?.token?.address || typeof item_2.token.address !== 'string') return null;
                                      const collection_1 = getCollectionByAddress(item_2.token.address as Address, chainId ?? fallbackChainId);
                                      if (!collection_1) return null;
                                      return collection_1.thumbnail ? <Image src={collection_1.thumbnail} alt={collection_1.name} className="w-5 h-5 rounded-full" /> : null;
                                    })()}
                                                <div className="flex flex-col">
                                                  <span className="text-white">
                                                    {item_2.token.name}
                                                  </span>
                                                  {(() => {
                                        const addrStr = (item_2?.token?.address || item_2?.token_id) as string | undefined;
                                        if (!addrStr || typeof addrStr !== 'string') return null;
                                        const contractAddr = addrStr.toLowerCase();
                                        const offerSummary = offerSummaries.get(contractAddr);
                                        if (offerSummary?.hasOffers) {
                                          return <span className="text-xs text-success-600">
                                                          {offerSummary.totalOffers} pending offer{offerSummary.totalOffers !== 1 ? 's' : ''}
                                                        </span>;
                                        }
                                        return null;
                                      })()}
                                                </div>
                                                {isERC1155 && <Badge size="sm" color="secondary" variant="flat">
                                                    ERC1155
                                                  </Badge>}
                                                {(() => {
                                      if (!item_2?.token?.address || typeof item_2.token.address !== 'string') return null;
                                      const collection_2 = getCollectionByAddress(item_2.token.address as Address, chainId ?? fallbackChainId);
                                      return collection_2 ? <Button as={Link} href={`../../../../../../marketplace/collection/${chainSettings[(collection_2.chainId ?? fallbackChainId) as ChainType].chainSlug}/${collection_2.slug ?? item_2.token.address}`} size="sm" variant="light" color="secondary" className="rounded-full p-0" radius="full" isIconOnly>
                                                      <Icon icon="healthicons:market-stall" width="15" height="15" style={{
                                          color: '#ff9400'
                                        }} />
                                                    </Button> : null;
                                    })()}
                                              </div>
                                            </TableCell>
                                            <TableCell>
                                              <div className="flex items-center gap-2">
                                                <span>
                                                  {(displayBalance ?? 0).toLocaleString()}
                                                </span>
                                                <span className="text-default-500">
                                                  {item_2.token.symbol}
                                                </span>
                                              </div>
                                            </TableCell>
                                          </TableRow>;
                            }}
                                    </TableBody>
                                  </Table>
                                </div>
                              </ScrollShadow>
                            </div>}
                        </motion.div> : <ScrollShadow size={15} className="h-full w-full">
                          <motion.div key="transactions" initial={{
                    opacity: 0,
                    x: 20
                  }} animate={{
                    opacity: 1,
                    x: 0
                  }} exit={{
                    opacity: 0,
                    x: -20
                  }} transition={{
                    type: "spring",
                    stiffness: 300,
                    damping: 30
                  }} className="h-full flex flex-col">
                            {transactions ? <Table isCompact fullWidth isStriped key="tx-table" removeWrapper>
                                <TableHeader>
                                  <TableColumn key="balance" allowsSorting className="px-0">
                                    &nbsp;
                                  </TableColumn>
                                  {chainId !== 2000 ? <TableColumn key="txMmethodCol">Method</TableColumn> : <TableColumn key="txMethod" width={0} className="hidden">
                                        &nbsp;
                                      </TableColumn>}
                                  <TableColumn key="txHash" allowsSorting>
                                    Hash
                                  </TableColumn>
                                  <TableColumn key="txValue" allowsSorting>
                                    Value
                                  </TableColumn>
                                </TableHeader>
                                <TableBody isLoading={isLoading} items={[transactions]}>
                                  {transactions.map((tx: Transaction) => <TableRow key={tx.hash}>
                                      <TableCell className="">
                                        {tx.result === "execution reverted" ? <Icon key={`${tx.hash}-icon-err`} icon="bxs:error" width="24" height="24" style={{
                              color: "#f90000"
                            }} /> : <Icon key={`${tx.hash}-icon-success`} icon="mdi:success-bold" width="24" height="24" style={{
                              color: "#00f928"
                            }} />}
                                      </TableCell>
                                      {chainId !== 2000 ? <TableCell>
                                          {String(tx.method).length > 12 ? <Tooltip delay={500} closeDelay={0} content={tx.method} key={`${tx.hash}-tooltip`} placement="top" className="text-white">
                                              {`${tx.method.slice(0, 6)}...${tx.method.slice(-6)}`}
                                            </Tooltip> : tx.method}
                                        </TableCell> : <TableCell className="hidden" key={`${tx.hash}-hidden`}>
                                            &nbsp;
                                          </TableCell>}
                                      <TableCell>
                                        <Link key={`${tx.hash}-link`} className="text-white" showAnchorIcon href={txLink(tx.hash, chainId ?? fallbackChainId)} isExternal>
                                          {truncAddress(tx.hash)}
                                        </Link>
                                      </TableCell>
                                      <TableCell key={`${tx.hash}-value-cell`}>
                                        {Number(tx.value) > 0 ? formatBalance(Number(tx.value) / 10 ** 18) : "0"}{" "}
                                        {chainSettings[chainId as ChainType ?? fallbackChainId].nativeCurrency.symbol}
                                      </TableCell>
                                    </TableRow>)}
                                </TableBody>
                              </Table> : null}
                          </motion.div>
                        </ScrollShadow>}
                    </AnimatePresence>
                  </div>
                </DrawerBody>
              </>}
        </DrawerContent>
      </Drawer>

      {/* Modals */}
      <BulkTransferModal />
      {/*<MantraListingsModal
        chainId={chainId ?? fallbackChainId}
        priceData={
          nativePrices && tokenPrices ? {
            nativePrices,
            tokenPrices,
          } : undefined
        }
       />*/}



    </>;
}